
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { getDebugInfo } from "./integrations/supabase/client";
import Index from "./pages/Index";
import IndustrySelector from "./pages/IndustrySelector";
import Survey from "./pages/Survey";
import Results from "./pages/Results";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import QRCodeGenerator from "./components/QRCodeGenerator";
import DomainRedirect from "./utils/DomainRedirect";

// Create a client with enhanced error logging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Updated to use onSettled instead of onError for compatibility with the latest Tanstack Query
      meta: {
        onSettled: (data: unknown, error: Error | null) => {
          if (error) {
            console.error('Query error:', error);
          }
        }
      }
    },
  },
});

const App = () => {
  // Preload necessary assets and enhanced logging
  React.useEffect(() => {
    try {
      const logoImg = new Image();
      logoImg.src = "/lovable-uploads/4940a812-afab-4024-9e71-e5facec11507.png";
      console.log("Preloading logo from:", window.location.origin + logoImg.src);
      
      // Log detailed environment information
      console.log("App mounted");
      console.log("Current URL:", window.location.href);
      console.log("Current pathname:", window.location.pathname);
      console.log("Current origin:", window.location.origin);
      console.log("Environment:", process.env.NODE_ENV);
      console.log("Debug info:", getDebugInfo());
      
      // Test network connectivity
      fetch(window.location.origin + '/favicon.ico')
        .then(res => console.log("Favicon fetch status:", res.status))
        .catch(err => console.error("Favicon fetch error:", err));
    } catch (error) {
      console.error("Error in App initialization:", error);
    }
    
    // Add document title
    document.title = "BEAMEvaluate Lite";
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <DomainRedirect />
            
            {/* Use BrowserRouter with explicit configuration */}
            <BrowserRouter>
              {/* Debug component for both dev and production */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ position: 'fixed', bottom: 0, right: 0, background: 'black', color: 'white', padding: '4px 8px', fontSize: '12px', zIndex: 9999 }}>
                  Path: {window.location.pathname}
                </div>
              )}
              
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/industry" element={<IndustrySelector />} />
                <Route path="/survey" element={<Survey />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/results" element={<Results />} />
                <Route path="/qr" element={<QRCodeGenerator />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                {/* Catch-all route for 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
